import { forwardRef, type HTMLAttributes } from "react";
import type { League } from "./league-badge.js";
import { cn } from "./lib/cn.js";

/**
 * Segmented control that flips between the six leagues. Used at the top of
 * the leaderboard page; bind `value` + `onChange` to the route param.
 */

const LEAGUES: League[] = ["tiny", "small", "medium", "large", "unbounded", "closed"];
const LABEL: Record<League, string> = {
 tiny: "Tiny",
 small: "Small",
 medium: "Medium",
 large: "Large",
 unbounded: "Unbounded",
 closed: "Closed",
};

export interface LeagueSwitcherProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
 value: League;
 onChange: (next: League) => void;
}

export const LeagueSwitcher = forwardRef<HTMLDivElement, LeagueSwitcherProps>(
 ({ value, onChange, className, ...props }, ref) => (
 <div
 ref={ref}
 role="tablist"
 aria-label="League"
 className={cn(
 "inline-flex gap-0.5 p-0.5 border border-[var(--pp-border)] bg-[var(--pp-bg-strong)]",
 className,
 )}
 {...props}
 >
 {LEAGUES.map((league) => {
 const active = league === value;
 return (
 <button
 key={league}
 type="button"
 role="tab"
 aria-selected={active}
 onClick={() => onChange(league)}
 className={cn(
 "px-3 py-1.5 text-[var(--pp-font-md)] transition-colors",
 "font-[var(--pp-font-family)] uppercase tracking-[var(--pp-tracking-caps)]",
 active
 ? "bg-[var(--pp-border)] text-[var(--pp-text)]"
 : "text-[var(--pp-text-muted)] hover:text-[var(--pp-text)]",
 )}
 >
 {LABEL[league]}
 </button>
 );
 })}
 </div>
 ),
);
LeagueSwitcher.displayName = "LeagueSwitcher";
